/**
 * Vision 2040 Translations Module (French)
 * @module vision2040
 * @language fr
 */

export const vision2040 = {
  vision2040Hero: {
    membersTarget: "Objectif Membres",
    countryTarget: "Objectif Pays",
    annualBeneficiaries: "Bénéficiaires Annuels"
  },
  vision2040TechTargets: {
    aiUsers: "400K+ utilisateurs",
    vrTrainers: "10K+ formateurs",
    appDownloads: "300K+ téléchargements",
    sustainabilityCountries: "80 pays"
  },
  strategicPillarsData: {
    expandTraining: "Étendre les Programmes de Formation en Leadership",
    certifiedTrainers: "10.000 Formateurs Certifiés",
    goldStandard: "Curriculum Standard d'Or",
    trainingTarget: "100K formés annuellement d'ici 2040",
    expandDetail1: "Étendre JCI Academy à 130+ pays",
    expandDetail2: "Développer 10K formateurs certifiés mondialement",
    expandDetail3: "Lancer plateforme de formation multilingue",
    expandDetail4: "Créer des parcours de leadership spécifiques par industrie",
    expandDetail5: "Établir des partenariats avec les universités",
    trainedYear: "Formés/An",
    aiDashboard: "Tableau de Bord IA",
    offlineApp: "App Mobile Offline-First",
    impactTracking: "Suivi d'Impact en Temps Réel",
    techTarget: "80% d'adoption numérique d'ici 2040",
    techDetail1: "Déployer plateforme d'expérience membre IA",
    techDetail2: "Lancer programme de simulation de leadership VR",
    techDetail3: "Créer tableau de bord de données mondial unifié",
    techDetail4: "Implémenter vérification d'impact blockchain",
    techDetail5: "Construire plateforme mobile-first accessible",
    users: "Utilisateurs",
    climateAction: "Intégration de l'Action Climatique",
    carbonNeutral: "Opérations Carbon Neutral",
    renewableEnergy: "100% Énergie Renouvelable",
    sustainabilityTarget: "Net-zéro d'ici 2030",
    sustainDetail1: "Atteindre opérations carbon neutral d'ici 2030",
    sustainDetail2: "Intégrer cartographie ODD pour tous les projets",
    sustainDetail3: "Créer programme de certification verte",
    sustainDetail4: "Partenariat avec organisations environnementales",
    sustainDetail5: "Développer curriculum d'alphabétisation climatique",
    riseProjects: "Projets RISE",
    genderParity: "Réalisation de la Parité des Genres",
    expandUnderrepresented: "Étendre aux Régions Sous-représentées",
    financialAccess: "Programmes d'Accès Financier",
    equityTarget: "50% parité des genres d'ici 2035",
    equityDetail1: "Atteindre 50% parité des genres en leadership",
    equityDetail2: "Étendre la présence dans les régions mal desservies",
    equityDetail3: "Créer programmes de bourses pour l'accès",
    equityDetail4: "Développer cadres de leadership inclusifs",
    equityDetail5: "Construire infrastructure d'accessibilité"
  },
  simulator: {
    factors: {
      conservativeGrowth: "Croissance annuelle de 2-3% des membres",
      limitedDigital: "Adoption limitée de la plateforme numérique",
      minimalPartnerships: "Nouveaux partenariats corporatifs minimaux",
      regionalChallenges: "Défis d'expansion régionale",
      moderateGrowth: "Croissance annuelle de 5-7% des membres",
      fullDigital: "Déploiement complet de la plateforme numérique",
      strategicPartnerships: "Partenariats corporatifs stratégiques",
      regionalExpansion: "Expansion régionale coordonnée",
      aggressiveGrowth: "Croissance annuelle de 10-15% des membres",
      aiEngagement: "Engagement des membres par IA",
      youthMovement: "Expansion virale du mouvement jeunesse",
      corporateSponsors: "Grands sponsors corporatifs"
    },
    risks: {
      targetsMissed: "Les objectifs Vision 2040 peuvent ne pas être atteints",
      reducedInfluence: "Influence et portée mondiale réduites",
      talentGaps: "Lacunes de talents dans les régions mal desservies",
      sustainedInvestment: "Investissement soutenu nécessaire",
      techInfrastructure: "Infrastructure technologique requise",
      regionalLeadership: "Leadership régional fort nécessaire",
      strainResources: "Les ressources peuvent être surchargées",
      qualityQuantity: "Équilibre qualité vs quantité",
      governanceChallenges: "Défis de gouvernance"
    }
  },
  vision2040: {
    title: "JCI Vision 2040",
    pageSubtitle: "Feuille de Route Stratégique vers 500.000 Jeunes Leaders",
    transformingFuture: "Transformer l'Avenir du Leadership Jeune",
    followProgress: "Suivez notre progression vers un réseau mondial de 500.000 jeunes leaders créant un impact positif durable d'ici 2040.",
    exploreVision: "Explorer Vision 2040",
    
    // Tab Labels
    fiiImpact: "Impact FII",
    timeline: "Chronologie",
    simulator: "Simulateur",
    globalView: "Vue Mondiale",
    
    // FII Impact Section
    fiiImpactTitle: "Impact FII d'ici 2040",
    fiiImpactDesc: "Améliorations projetées de l'Indice d'Investissement Futur si JCI exécute la stratégie Vision 2040",
    globalFIIPoints: "Points FII Mondiaux",
    africaMeFII: "Points FII Afrique/MO",
    inequalityReduction: "Réduction des Inégalités",
    regionalGapClosure: "Fermeture de l'écart régional",
    highest: "le plus élevé",
    mentalHealthPillar: "Santé Mentale",
    workforceDevelopment: "Développement de la Main-d'œuvre",
    entrepreneurshipPillar: "Entrepreneuriat",
    projectedReach: "Portée Projetée",
    fiiImpactLabel: "Impact FII",
    primaryFocus: "Focus primaire",
    regionalFIIProjectionsTitle: "Projections FII Régionales: 2025 vs 2040",
    current2025: "Actuel 2025",
    projected2040: "Projeté 2040",
    maintainLead: "Maintenir l'avance",
    strongGrowth: "Forte croissance",
    accelerating: "En accélération",
    highestImprovement: "Plus grande amélioration",
    sourceFII: "Source: Plan Stratégique JCI Vision 2040 | Projections de l'Indice d'Investissement Futur 2024",
    
    // Simulator Section
    simulatorTitle: "Simulateur de Scénarios Vision 2040",
    simulatorSubtitle: "Explorez trois futurs possibles pour JCI. Que se passe-t-il si nous dépassons les attentes—ou si nous échouons?",
    baseline2025: "Ligne de Base 2025",
    conservative: "Conservateur",
    conservativeDesc: "Et si la croissance stagne? Adoption technologique limitée, partenariats minimaux, vents économiques contraires.",
    moderate: "Modéré (Objectif)",
    moderateDesc: "Trajectoire de croissance planifiée avec adoption numérique attendue et partenariats stratégiques.",
    aggressive: "Agressif",
    aggressiveDesc: "Croissance maximale avec adoption technologique complète, partenariats étendus et expansion virale.",
    probability: "Probabilité",
    scenarioOutcomes: "Résultats du Scénario",
    vsTarget: "vs objectif",
    onTarget: "Sur Objectif",
    keySuccessFactors: "Facteurs Clés de Succès",
    risksAndChallenges: "Risques et Défis",
    growthTrajectory: "Comparaison de Trajectoire de Croissance",
    scenario: "Scénario",
    targetModerate: "Objectif (Modéré)",
    comparisonSummary: "Résumé de Comparaison de Scénarios",
    metric: "Métrique",
    baseline: "Ligne de Base",
    target2040: "Objectif 2040",
    sourceSimulator: "Source: Plan Stratégique JCI Vision 2040 | Les scénarios sont des projections illustratives",
    
    // Growth Visualization Section
    growthVisualization: "Visualisation de la Croissance",
    growthDesc: "Trajectoire de croissance stratégique de la ligne de base 2025 aux objectifs 2040",
    growthVisualizationDesc: "Trajectoire de croissance stratégique de la ligne de base 2025 aux objectifs 2040",
    progress: "Progrès",
    members: "Membres",
    membersK: "Membres (K)",
    targetK: "Objectif (K)",
    countries: "Pays",
    localOrganizations: "Organisations Locales",
    annualProjects: "Projets Annuels",
    annualBeneficiaries: "Bénéficiaires Annuels",
    current: "Actuel",
    target: "Objectif",
    growth: "Croissance",
    cagr: "TCAC",
    cagrMembers: "~8-10% TCAC",
    nationsGrowth: "+86 nations",
    expansionRegions: "Afrique subsaharienne, Moyen-Orient, Asie du Sud-Est",
    moreGrassrootsHubs: "Plus de centres de base",
    projectOrgAverage: "~1 projet/org en moyenne",
    youthPopulation: "~1,2% de la population jeunesse mondiale",
    priority: { critical: "Critique", high: "Élevé", medium: "Moyen", low: "Faible" },
    globalPresence: "Présence Mondiale JCI",
    globalMembers: "Membres Mondiaux",
    activeCountries: "Pays Actifs",
    averageGrowth: "Croissance Moyenne",
    regionalDistribution: "Distribution Régionale",
    globalPerformance: "Performance Mondiale",
    performanceComparison: "Comparaison entre performance actuelle vs objectifs Vision 2040",
    detailedAnalysis: "Analyse Régionale Détaillée",
    featuredCountries: "Pays en Vedette",
    achievements: "Réalisations",
    challenges: "Défis",
    mainInitiatives: "Initiatives Principales",
    leadershipDevelopment: "Excellence en Développement du Leadership",
    technologyInnovation: "Technologie et Innovation Numérique",
    sustainabilityFocus: "Focus sur la Durabilité et l'Impact",
    globalNetworkGrowth: "Expansion du Réseau Mondial",
    strategicPillars: "Piliers Stratégiques",
    strategicPillarsTitle: "4 Piliers Stratégiques pour 2040",
    strategicPillarsSubtitle: "Approche intégrée pour transformer l'opportunité des jeunes mondialement",
    strategicPillarsDesc: "Notre feuille de route est construite sur quatre piliers interconnectés qui ensemble transformeront l'opportunité des jeunes dans le monde",
    viewAllInitiatives: "Voir Toutes les Initiatives",
    allInitiatives: "Toutes les Initiatives",
    pillar1Title: "Santé Mentale et Bien-être",
    pillar2Title: "Développement de la Main-d'œuvre et Compétences",
    pillar3Title: "Entrepreneuriat et Innovation",
    pillar4Title: "Partenariat Mondial et Mise à l'Échelle",
    sustainability: "Durabilité",
    equityInclusion: "Équité et Inclusion",
    current2040Target: "Actuel → Objectif 2040",
    milestoneRoadmap: "Feuille de Route des Jalons",
    milestoneDesc: "Suivez notre parcours de l'état actuel à la réalisation de Vision 2040",
    currentState: "État Actuel",
    currentStateDesc: "Notre point de départ - 148.000 membres dans 114 pays",
    midpointTarget: "Objectif Intermédiaire",
    midpointTargetDesc: "Marque de mi-parcours - 300.000 membres dans 150 pays",
    accelerationPhase: "Phase d'Accélération",
    accelerationPhaseDesc: "Mise à l'échelle rapide - 400.000 membres dans 175 pays",
    visionAchieved: "Vision Réalisée",
    visionAchievedDesc: "Objectif complet - 500.000 membres dans 200 pays",
    inProgress: "En Cours",
    upcoming: "À Venir",
    completed: "Terminé",
    planned: "Planifié",
    orgs: "Orgs",
    projects: "Projets",
    beneficiaries: "Bénéficiaires",
    keyInitiatives: "Initiatives Clés",
    needed: "Nécessaire",
    growthRequired: "croissance requise",
    sourceStrategicPlan: "Source: Plan Stratégique JCI Vision 2040",
    timelineTitle: "Chronologie Vision 2040",
    yearsRemaining: "Années Restantes",
    historicalProgress: "Progrès Historique",
    jciFounded: "JCI Fondée",
    foundedDesc: "Junior Chamber International établie à St. Louis, Missouri",
    globalExpansion: "Expansion Mondiale",
    globalExpansionDesc: "Expansion à 100+ pays dans le monde",
    foundationYear: "Année de Fondation",
    foundationYearDesc: "Transformation numérique et mise à l'échelle du programme RISE",
    programs: "Programmes",
    implementationProgress: "Progrès de Mise en Œuvre",
    digitalPlatform: "Plateforme Numérique",
    techTarget: "Objectif: 80%+ des membres utilisant la plateforme numérique d'ici 2040",
    aiIntegration: "Intégration IA",
    aiDesc: "Expérience membre personnalisée et recommandations de programme",
    vrTraining: "Formation Leadership VR",
    vrTrainingDesc: "Simulations immersives pour le développement du leadership",
    aiDashboard: "Tableau de Bord IA",
    aiDashboardDesc: "Expérience membre personnalisée avec recommandations IA",
    sustainabilityGoals: "Objectifs de Durabilité",
    carbonNeutral: "Opérations Carbon Neutral",
    carbonNeutralDesc: "100% compensation carbone d'ici 2030",
    sdgAlignment: "Alignement ODD",
    sdgDesc: "Tous les programmes mappés aux Objectifs de Développement Durable de l'ONU",
    techRoadmap: "Feuille de Route Technologique",
    techRoadmapDesc: "Jalons de transformation numérique sur notre chemin vers 2040",
    mobileApp: "Application Mobile",
    mobileAppDesc: "Expérience mobile offline-first pour tous les membres",
    sustainabilityTracking: "Suivi de Durabilité",
    sustainabilityTrackingDesc: "Surveillance d'impact environnemental en temps réel",
    complete: "Terminé"
  },
  vision2040Extended: {
    fiiImpactTab: "Impact FII",
    timeline: "Chronologie",
    simulator: "Simulateur",
    globalView: "Vue Mondiale",
    milestoneTitle: "Feuille de Route des Jalons vers 2040",
    milestoneSubtitle: "Objectifs clés et indicateurs de progrès sur quatre phases stratégiques",
    milestoneRoadmap: "Feuille de Route des Jalons",
    milestoneRoadmapDesc: "Suivez notre parcours de l'état actuel à la réalisation de Vision 2040",
    currentState: "État Actuel",
    currentStateDesc: "Notre point de départ - 175.000 membres dans 115 pays",
    midpointTarget: "Objectif Intermédiaire",
    midpointTargetDesc: "Marque de mi-parcours - 300.000 membres dans 130 pays",
    accelerationPhase: "Phase d'Accélération",
    accelerationPhaseDesc: "Mise à l'échelle rapide - 400.000 membres dans 140 pays",
    visionAchieved: "Vision Réalisée",
    visionAchievedDesc: "Objectif complet - 500.000 membres dans 150 pays",
    inProgress: "En Cours",
    upcoming: "À Venir",
    completed: "Terminé",
    planned: "Planifié",
    target: "Objectif",
    members: "Membres",
    countries: "Pays",
    localOrganizations: "Organisations Locales",
    orgs: "Orgs",
    projects: "Projets",
    beneficiaries: "Bénéficiaires",
    keyInitiatives: "Initiatives Clés",
    current: "Actuel",
    needed: "Nécessaire",
    growthRequired: "croissance requise",
    sourceStrategicPlan: "Source: Plan Stratégique JCI Vision 2040",
    fiiImpactTitle: "Impact FII d'ici 2040",
    fiiImpactDesc: "Améliorations projetées de l'Indice d'Investissement Futur si JCI exécute la stratégie Vision 2040",
    globalFIIPoints: "Points FII Mondiaux",
    africaMeFII: "Points FII Afrique/MO",
    inequalityReduction: "Réduction des Inégalités",
    regionalGapClosure: "Fermeture de l'écart régional",
    highest: "le plus élevé",
    mentalHealthPillar: "Santé Mentale",
    workforceDevelopment: "Développement de la Main-d'œuvre",
    entrepreneurshipPillar: "Entrepreneuriat",
    projectedReach: "Portée Projetée",
    fiiImpactLabel: "Impact FII",
    primaryFocus: "Focus primaire",
    regionalFIIProjectionsTitle: "Projections FII Régionales: 2025 vs 2040",
    current2025: "Actuel 2025",
    projected2040: "Projeté 2040",
    maintainLead: "Maintenir l'avance",
    strongGrowth: "Forte croissance",
    accelerating: "En accélération",
    highestImprovement: "Plus grande amélioration",
    sourceFII: "Source: Plan Stratégique JCI Vision 2040 | Projections de l'Indice d'Investissement Futur 2024",
    timelineTitle: "Chronologie Vision 2040",
    timelineDesc: "Contexte historique et jalons futurs sur notre parcours vers 2040",
    yearsRemaining: "Années Restantes",
    historicalProgress: "Progrès Historique",
    jciFounded: "JCI Fondée",
    foundedDesc: "Junior Chamber International établie",
    globalExpansion: "Expansion Mondiale",
    globalExpansionDesc: "Expansion à 100+ pays dans le monde",
    foundationYearDesc: "Transformation numérique et mise à l'échelle du programme RISE",
    programs: "Programmes",
    implementationProgress: "Progrès de Mise en Œuvre",
    simulatorTitle: "Simulateur de Scénarios Vision 2040",
    simulatorSubtitle: "Explorez trois futurs possibles pour JCI. Que se passe-t-il si nous dépassons les attentes—ou si nous échouons?",
    baseline2025: "Ligne de Base 2025",
    conservative: "Conservateur",
    conservativeDesc: "Et si la croissance stagne? Adoption technologique limitée, partenariats minimaux, vents économiques contraires.",
    moderate: "Modéré (Objectif)",
    moderateDesc: "Trajectoire de croissance planifiée avec adoption numérique attendue et partenariats stratégiques.",
    aggressive: "Agressif",
    aggressiveDesc: "Croissance maximale avec adoption technologique complète, partenariats étendus et expansion virale.",
    probability: "Probabilité",
    scenarioOutcomes: "Résultats du Scénario",
    annualProjects: "Projets Annuels",
    vsTarget: "vs objectif",
    onTarget: "Sur Objectif",
    keySuccessFactors: "Facteurs Clés de Succès",
    risksAndChallenges: "Risques et Défis",
    growthTrajectory: "Comparaison de Trajectoire de Croissance",
    scenario: "Scénario",
    targetModerate: "Objectif (Modéré)",
    comparisonSummary: "Résumé de Comparaison de Scénarios",
    metric: "Métrique",
    baseline: "Ligne de Base",
    target2040: "Objectif 2040",
    sourceSimulator: "Source: Plan Stratégique JCI Vision 2040 | Les scénarios sont des projections illustratives à des fins de planification stratégique",
    simulatorData: {
      factors: {
        conservativeGrowth: "Croissance annuelle de 2-3% des membres",
        limitedDigital: "Adoption limitée de la plateforme numérique",
        minimalPartnerships: "Nouveaux partenariats corporatifs minimaux",
        moderateGrowth: "Croissance annuelle de 5-7% des membres",
        fullDigital: "Déploiement complet de la plateforme numérique",
        strategicPartnerships: "Partenariats corporatifs stratégiques",
        aggressiveGrowth: "Croissance annuelle de 10-15% des membres",
        aiEngagement: "Engagement des membres par IA",
        youthMovement: "Expansion virale du mouvement jeunesse"
      },
      risks: {
        targetsMissed: "Les objectifs Vision 2040 peuvent ne pas être atteints",
        reducedInfluence: "Influence et portée mondiale réduites",
        talentGaps: "Lacunes de talents dans les régions mal desservies",
        moderateRisk: "Risque d'exécution modéré demeure",
        resourceConstraints: "Contraintes de ressources possibles",
        competitionRisk: "Concurrence d'autres organisations",
        executionChallenges: "Défis d'exécution significatifs",
        fundingRequirements: "Exigences de financement élevées",
        marketChanges: "Les conditions du marché peuvent changer"
      }
    },
    globalPresenceTitle: "Présence Mondiale JCI",
    globalMembers: "Membres Mondiaux",
    activeCountries: "Pays Actifs",
    averageGrowth: "Croissance Moyenne",
    regionalDistribution: "Distribution Régionale",
    globalPerformance: "Performance Mondiale",
    performanceComparison: "Comparaison entre performance actuelle vs objectifs Vision 2040",
    detailedAnalysis: "Analyse Régionale Détaillée",
    achievements: "Réalisations",
    challenges: "Défis",
    growth: "Croissance",
    priority: { critical: "Critique", high: "Élevé", medium: "Moyen", low: "Faible" },
    milestoneInitiatives: {
      year2025: { initiative1: "Expansion de la Plateforme Numérique", initiative2: "Continuation du programme RISE", initiative3: "Ligne de base FII établie", initiative4: "Stratégies de croissance régionale" },
      year2030: { initiative1: "Opérations carbon-neutral", initiative2: "Tableau de bord membres IA", initiative3: "50% parité des genres atteinte", initiative4: "LMS numérique entièrement déployé" },
      year2035: { initiative1: "10.000+ formateurs certifiés", initiative2: "Formation leadership VR déployée", initiative3: "Écart FII Afrique/MO 50% fermé", initiative4: "App mobile 300K+ téléchargements" },
      year2040: { initiative1: "Réseau de 500K jeunes leaders", initiative2: "FII mondial amélioré de 4-7 points", initiative3: "80%+ adoption plateforme numérique", initiative4: "Opportunité des jeunes transformée" }
    },
    pillarData: {
      mentalHealth: { current: "145 projets RISE", projection: "5.000+ projets RISE", reach: "50M+ atteints", fiiImpact: "+2-3 points", focus: "Écart santé mentale Afrique/MO" },
      workforce: { current: "~50K formés", projection: "200K+ jeunes formés", reach: "80% taux d'emploi", fiiImpact: "+1-2 points", focus: "Parcours STEM/numériques" },
      entrepreneurship: { current: "1K entrepreneurs", projection: "50K+ soutenus", reach: "500M$+ capital mobilisé", fiiImpact: "+1-2 points", focus: "100K+ formés" }
    },
    regionNames: { europe: "Europe", asiaPacific: "Asie et le Pacifique", americas: "Amérique", africaME: "Afrique et Moyen-Orient" },
    radarSubjects: { leadership: "Leadership", technology: "Technologie", sustainability: "Durabilité", partnerships: "Partenariats", innovation: "Innovation", impact: "Impact" },
    globalRegions: {
      americas: { name: "Amérique", achievements: ["8,0% de l'adhésion mondiale", "Réseau LO solide"], challenges: ["Rétention des membres", "Diversité régionale"] },
      europe: { name: "Europe", achievements: ["11,1% de l'adhésion mondiale", "Leadership en durabilité"], challenges: ["Démographie vieillissante", "Stagnation de croissance"] },
      asiaPacific: { name: "Asie et le Pacifique", achievements: ["70,7% de l'adhésion mondiale", "Plus grand potentiel de croissance"], challenges: ["Barrières culturelles", "Infrastructure numérique"] },
      africaME: { name: "Afrique et Moyen-Orient", achievements: ["10,3% de l'adhésion", "Avantage population jeune"], challenges: ["Ressources limitées", "Lacunes de connectivité"] }
    }
  }
} as const;
